
import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AuthProvider } from '@/contexts/AuthContext';
import Layout from '@/components/layout/Layout';
import OptimizedLoading from '@/components/ui/OptimizedLoading';

// Lazy load components to improve performance
const NotFound = lazy(() => import('@/pages/NotFound'));
const ChatBot = lazy(() => import('@/components/chat/ChatBot'));
const AdminAIHelper = lazy(() => import('@/pages/AdminAIHelper'));
const AIFileSettings = lazy(() => import('@/pages/AIFileSettings'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Auth = lazy(() => import('@/pages/Auth'));
const Settings = lazy(() => import('@/pages/Settings'));
const Projects = lazy(() => import('@/pages/Projects'));
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'));
const TeamMembers = lazy(() => import('@/pages/TeamMembers'));
const TeamMemberDetail = lazy(() => import('@/pages/TeamMemberDetail'));
const ScopeOfWorksUploader = lazy(() => import('@/pages/ScopeOfWorksUploader'));
const ScopeOfWorksGenerator = lazy(() => import('@/pages/ScopeOfWorksGenerator'));
const ScopeOfWorksViewer = lazy(() => import('@/pages/ScopeOfWorksViewer'));
const NHFCodeViewer = lazy(() => import('@/pages/NHFCodeViewer'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const SystemSettings = lazy(() => import('@/pages/SystemSettings'));
const ContentManager = lazy(() => import('@/pages/ContentManager'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const Profile = lazy(() => import('@/pages/Profile'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const Help = lazy(() => import('@/pages/Help'));

// New pages for company site
const Services = lazy(() => import('@/pages/Services'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const CaseStudy = lazy(() => import('@/pages/CaseStudy'));

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Suspense fallback={<OptimizedLoading fullScreen message="Loading application..." />}>
          <Routes>
            <Route path="/" element={<Layout />}>
              {/* Dashboard */}
              <Route index element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Dashboard />
                </Suspense>
              } />
              <Route path="dashboard" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Dashboard />
                </Suspense>
              } />
              
              {/* Authentication */}
              <Route path="auth" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Auth />
                </Suspense>
              } />
              
              {/* User Settings */}
              <Route path="settings" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Settings />
                </Suspense>
              } />
              <Route path="profile" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Profile />
                </Suspense>
              } />
              <Route path="notifications" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Notifications />
                </Suspense>
              } />
              <Route path="help" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Help />
                </Suspense>
              } />
              
              {/* Projects */}
              <Route path="projects" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Projects />
                </Suspense>
              } />
              <Route path="projects/:id" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <ProjectDetail />
                </Suspense>
              } />
              
              {/* Team */}
              <Route path="team" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <TeamMembers />
                </Suspense>
              } />
              <Route path="team/:id" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <TeamMemberDetail />
                </Suspense>
              } />
              
              {/* Scope of Works */}
              <Route path="scope-uploader" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <ScopeOfWorksUploader />
                </Suspense>
              } />
              <Route path="scope-generator" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <ScopeOfWorksGenerator />
                </Suspense>
              } />
              <Route path="scope/:id" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <ScopeOfWorksViewer />
                </Suspense>
              } />
              
              {/* NHF Codes */}
              <Route path="nhf-codes" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <NHFCodeViewer />
                </Suspense>
              } />
              
              {/* Admin Routes */}
              <Route path="admin" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <AdminDashboard />
                </Suspense>
              } />
              <Route path="admin/users" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <UserManagement />
                </Suspense>
              } />
              <Route path="admin/settings" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <SystemSettings />
                </Suspense>
              } />
              <Route path="admin/content" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <ContentManager />
                </Suspense>
              } />
              <Route path="admin/analytics" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Analytics />
                </Suspense>
              } />
              
              {/* AI Assistants */}
              <Route path="admin-ai-helper" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <AdminAIHelper />
                </Suspense>
              } />
              <Route path="ai-file-settings" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <AIFileSettings />
                </Suspense>
              } />
              
              {/* Company Pages */}
              <Route path="services" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Services />
                </Suspense>
              } />
              <Route path="about" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <About />
                </Suspense>
              } />
              <Route path="contact" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <Contact />
                </Suspense>
              } />
              <Route path="case-study" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <CaseStudy />
                </Suspense>
              } />
              
              {/* Catch all */}
              <Route path="*" element={
                <Suspense fallback={<OptimizedLoading />}>
                  <NotFound />
                </Suspense>
              } />
            </Route>
          </Routes>
          
          <Suspense fallback={null}>
            <ChatBot />
          </Suspense>
          <Toaster />
        </Suspense>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
